import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Grid,
  Button,
  TextField,
  Box,
  Divider,
  Snackbar,
  Alert,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IconTableExport } from "@tabler/icons-react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { convertUtcToIST } from "utils/ISTConvert";
import handleExport from "utils/exportData";
import PropTypes from "prop-types";

const AttendanceDialog = ({
  open,
  onClose,
  userData,
  onSuccess,
  heading,
  headerBackgroundColors,
}) => {
  // const attendanceArray = Array.isArray(userData?.attendance_data) ? userData.attendance_data : [];
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [message, setMessage] = useState({
    status: "",
    title: "",
    msg: "",
  });
  const [rowSelection, setRowSelection] = useState({});

  const fetchAttendanceData = async (pageIndex = 0, pageSize = 10) => {
    if (!userData?.user_id) return;

    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        user_id: userData.user_id,
      });

      if (userData.start_date) queryParams.append("start_date", userData.start_date);
      if (userData.end_date) queryParams.append("end_date", userData.end_date);

      if (sorting.length > 0) {
        const sortField = sorting[0].id;
        const sortOrder = sorting[0].desc ? "desc" : "asc";
        queryParams.append("sortField", sortField);
        queryParams.append("sortOrder", sortOrder);
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/attendance/attendance-data?${queryParams.toString()}`,
        { withCredentials: true }
      );

      if (res.data.status === 200) {
        setAttendanceData(res.data.data);
        setRowCount(res.data.total ?? 0);
      }
    } catch (err) {
      console.error("Error fetching attendance data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && userData?.user_id) {
      fetchAttendanceData();
    }
  }, [pagination.pageIndex, pagination.pageSize, sorting, userData?.user_id, open]);

  const [rotate, setRotate] = React.useState(0);
  const handleMouseEnter = () => {
    setRotate(360);
  };

  const handleMouseLeave = () => {
    setRotate(180);
  };

  const columns = [
    {
      accessorKey: "attendance_date",
      header: "Attendance Date",
      Cell: ({ row }) => convertUtcToIST(row.original.attendance_date) ?? "-",
    },
    {
      accessorKey: "status",
      header: "Status",
      Cell: ({ row }) => (
        <Chip
          label={row.original.status}
          color={
            row.original.status === "Present"
              ? "success"
              : row.original.status === "Leave"
                ? "warning"
                : "error"
          }
          sx={{
            fontWeight: "bold",
            textTransform: "capitalize",
            color: "white",
          }}
        />
      ),
    },
    {
      accessorKey: "leave_type",
      header: "Leave Type",
      Cell: ({ row }) => (row.original.leave_type === "On Duty" ? "-" : row.original.leave_type) || "-",
    },
    {
      accessorKey: "logIn_time",
      header: "LogIn Time",
      Cell: ({ row }) => {
        const time = row.original.logIn_time;
        if (!time) return "00:00:00";
        const [hour, minute] = time.split(":");
        const h = parseInt(hour);
        const suffix = h >= 12 ? "PM" : "AM";
        const formatted = `${((h + 11) % 12) + 1}:${minute} ${suffix}`;
        return formatted;
      },
    },
    {
      accessorKey: "logOut_time",
      header: "LogOut Time",
      Cell: ({ row }) => {
        const time = row.original.logOut_time;
        if (!time) return "00:00:00";
        const [hour, minute] = time.split(":");
        const h = parseInt(hour);
        const suffix = h >= 12 ? "PM" : "AM";
        const formatted = `${((h + 11) % 12) + 1}:${minute} ${suffix}`;
        return formatted;
      },
    },
  ];

  const handleExportData = async () => {
    try {
      let formattedData = [];

      if (rowSelection && Object.keys(rowSelection).length > 0) {
        formattedData = exportData.filter((item) => rowSelection[item.id]);
      } else {
        const queryParams = new URLSearchParams({
          user_id: userData.user_id,
          page: 1,
          limit: 10000,
        });

        if (userData.start_date) queryParams.append("start_date", userData.start_date);
        if (userData.end_date) queryParams.append("end_date", userData.end_date);

        if (sorting.length > 0) {
          const sortField = sorting[0].id;
          const sortOrder = sorting[0].desc ? "desc" : "asc";
          queryParams.append("sortField", sortField);
          queryParams.append("sortOrder", sortOrder);
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/attendance/attendance-data?${queryParams.toString()}`,
          { withCredentials: true }
        );

        if (res.data.status === 200) {
          formattedData = res.data.data;
        }
      }

      if (formattedData.length === 0) {
        console.log("No data found to export");
        setMessage({
          status: "error",
          title: "Error",
          msg: "No data found to export.",
        });
        return;
      }

      await handleExport(formattedData, "Attendance_List_User_Data");

      setMessage({
        status: "success",
        title: "Success",
        msg: "Excel downloaded successfully!",
      });
    } catch (error) {
      console.error(" Export error:", error);
      setMessage({
        status: "error",
        title: "Error",
        msg: "Something went wrong during export.",
      });
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            boxShadow: 6,
            position: "relative",
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={onClose}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: "absolute",
            top: -18,
            right: -16,
            backgroundColor: "#fff",
            color: "#ff0a54",
            boxShadow: 1,
            zIndex: 1500,
            p: 1.5,
            "&:hover": {
              backgroundColor: "#ff0a54",
              color: "#fff",
            },
            transition: "transform 0.5s ease",
            transform: `rotate(${rotate}deg)`,
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            background: `linear-gradient(45deg, ${
              headerBackgroundColors ? headerBackgroundColors[0] : "#2196f3"
            }, ${headerBackgroundColors ? headerBackgroundColors[1] : "#21cbf3"})`, // blue gradient
            color: "white",
            fontWeight: "bold",
            textAlign: "center",
            position: "relative",
            zIndex: 1400,
            py: 2,
          }}
        >
          {heading ? heading : "Leave Request Details"}
        </DialogTitle>
        <DialogContent>
          {/* {loading || !attendanceArray ? (
            <Typography>Loading...</Typography>
          ) : ( */}
          <Box>
            <Grid container spacing={5}>
              <Grid size={{ xs: 12, md: 12 }} className="listTable">
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                  <Paper
                    elevation={2}
                    sx={{
                      width: "100%",
                      overflowX: "auto",
                      backgroundColor: "#fff",
                      boxShadow: "none",
                      p: 2,
                    }}
                  >
                    <MaterialReactTable
                      columns={columns}
                      data={attendanceData}
                      // getRowId={(row) => row.id}
                      enableGlobalFilter={false}
                      enableFullScreenToggle={false}
                      enableDensityToggle={false}
                      initialState={{ showColumnFilters: false }}
                      layoutMode="grid"
                      enableRowNumbers
                      manualFiltering
                      manualPagination
                      manualSorting
                      onPaginationChange={setPagination}
                      onSortingChange={setSorting}
                      rowCount={rowCount}
                      state={{
                        // columnFilters,
                        // globalFilter,
                        // isLoading,
                        loading,
                        pagination,
                        // showAlertBanner: isError,
                        // showProgressBars: isRefetching,
                        sorting,
                      }}
                      muiTableHeadProps={{
                        sx: {
                          padding: "0px", // won't apply directly to <thead>, but useful for general structure
                          backgroundColor: "#011F3A", // background on <thead> element itself
                        },
                      }}
                      enableColumnActions={false}
                      muiTableHeadCellProps={{
                        sx: {
                          backgroundColor: "#004030",
                          color: "#fff",
                          // fontSize: '16px',
                          fontWeight: "bold",
                          position: "sticky", // Make header sticky
                          top: 0, // Stick it at the top
                          zIndex: 1, // Ensure it stays
                          "& .MuiTableSortLabel-root": {
                            "& .MuiTableSortLabel-icon": {
                              color: "#fff !important",
                            },
                          },
                          "& .MuiInputBase-input": {
                            color: "#fff !important",
                            fontSize: "12px",
                            "::placeholder": {
                              color: "#e0e0e0", // Default (blurred) placeholder color
                              opacity: 1,
                            },
                          },
                          "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                            color: "#ffffff", // Focused placeholder color
                          },
                          "& .MuiIconButton-root": {
                            color: "#fff",
                          },
                          "& .MuiInput-underline:before": {
                            borderBottomColor: "#e0e0e0",
                          },
                          "& .MuiInput-underline:after": {
                            borderBottomColor: "#fff",
                          },
                          "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                            borderBottomColor: "#fff",
                          },

                          "& .MuiCheckbox-root": {
                            color: "#fff",
                          },
                        },
                      }}
                      muiTableBodyCellProps={{
                        sx: {
                          padding: "8px", // reduce from default 16px
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                      enableExpandAll={false}
                      enableExpanding
                      getRowCanExpand={(row) => row.original.status === "Leave"}
                      renderDetailPanel={({ row }) => (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            padding: "16px",
                            fontSize: "14px",
                            backgroundColor: "#f3f3f3",
                            borderLeft: "4px solid #ff9800",
                            lineHeight: 1.6,
                          }}
                        >
                          <Typography>
                            <strong>Reason:</strong> {row.original.reason || "N/A"}
                          </Typography>
                        </Box>
                      )}
                      muiTableBodyRowProps={({ row }) => ({
                        sx: {
                          backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0", // soft alternating colors
                        },
                      })}
                      muiExpandButtonProps={({ row }) => ({
                        sx: { marginLeft: "-10px" }, // reduce spacing
                        title: "", // remove tooltip
                      })}
                      muiExpandAllButtonProps={{
                        sx: {
                          visibility: "hidden", // hides expand header cell
                          width: "10px",
                        },
                      }}
                      displayColumnDefOptions={{
                        "mrt-row-numbers": {
                          size: 40,
                          maxSize: 40,
                          position: "left", // ensure it's on the left
                        },
                        "mrt-row-expand": {
                          size: 20,
                          maxSize: 20,
                        },
                      }}
                      renderTopToolbarCustomActions={({ table }) => (
                        <Box
                          sx={{
                            display: "flex",
                            gap: "1rem",
                            p: "0.5rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <Tooltip title={"Export data to excel sheet"} arrow>
                            <Button
                              onClick={handleExportData}
                              sx={{
                                background:
                                  "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                                color: "#f4f4f4",
                                borderRadius: "8px",
                                padding: "10px 20px",
                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                                transition: "background 0.3s, transform 0.3s",
                                "&:hover": {
                                  background:
                                    "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%))",
                                  transform: "scale(1.05)",
                                },
                                "&:active": {
                                  transform: "scale(0.95)",
                                },
                              }}
                              startIcon={<IconTableExport />}
                            >
                              {"Export To Excel"}
                            </Button>
                          </Tooltip>
                        </Box>
                      )}
                      muiTopToolbarProps={{
                        sx: {
                          backgroundColor: "#fff",
                          boxShadow: "none",
                        },
                      }}
                    />
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          </Box>
          {/* )} */}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(message.status)}
        autoHideDuration={3000}
        onClose={() => setMessage({ status: "", title: "", msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setMessage({ status: "", title: "", msg: "" })}
          severity={message.status}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {message.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

AttendanceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userData: PropTypes.shape({
    user_id: PropTypes.number.isRequired,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }).isRequired,
  onSuccess: PropTypes.func.isRequired,
  heading: PropTypes.string.isRequired,
  headerBackgroundColors: PropTypes.string,
};

export default AttendanceDialog;
